import { Inject, Injectable } from '@angular/core';
import { AbcCore, CompareHelper } from '@barthauer/abc/core';
import { AuthConfigService, UserService } from '@barthauer/abc/core/authentication';
import {
  ObjectKey,
  PersistMode,
  PersistObjectsModel,
  Query,
  QueryColumnSortOrder,
  QueryObjectsModel,
  QueryParameter,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import {
  DialogResult,
  MessageBoxButtons,
  MessageBoxService,
  MessageMode,
  MessageSettings,
} from '@barthauer/abc/messages';
import { forkJoin, Observable, of, Subject } from 'rxjs';
import { catchError, exhaustMap, map, startWith, take, tap } from 'rxjs/operators';
import { MUser } from '../models/data.model';
import { ArrayMap, UserViewModel, ViewModelBase } from '../models/view.model';
import { ContextService } from './context.service';

@Injectable()
export class DataService {
  constructor(
    @Inject('ObjectSpaceMaster') private objectSpaceMaster: string,
    private authService: AuthConfigService,
    private userService: UserService,
    private coreService: AbcCore,
    private messageBoxService: MessageBoxService,
    private contextService: ContextService
  ) {}

  queryUser(
    component: {
      errorMessage: string;
      isLoading: boolean;
      user?: UserViewModel;
    },
    refreshData$: Subject<void>
  ): Observable<void> {
    const queries = [this.createMUserQuery()];

    return this.createQueryAndRefreshObservable<UserViewModel>(
      component,
      refreshData$,
      (user) => (component.user = user),
      () => {
        this.contextService.users.clear();
      },
      (data, id) => {
        if (!CompareHelper.isEmptyObject(data[0])) {
          const user = this.setUsers(data[0].Users, id);

          if (user) {
            component.user = user;
          }
        }
      },
      () => {
        if (!component.user) {
          this.contextService.setResource((res) => (component.errorMessage = res.get('UserSettings-NoMUserFound')));
        }
      },
      queries
    );
  }

  private createQueryAndRefreshObservable<T extends ViewModelBase>(
    component: {
      errorMessage: string;
      isLoading: boolean;
    },
    refreshData$: Subject<void>,
    setDataItem: (item: T | undefined) => void,
    reset: () => void,
    handleData: (data: any[], id: string) => void,
    validate: () => void,
    queriesMasterObjectSpace: Query[]
  ): Observable<void> {
    const queryMaster = new QueryObjectsModel();
    queryMaster.ObjectSpaceName = this.objectSpaceMaster;
    queryMaster.ObjectQueries = queriesMasterObjectSpace;
    const queryMasterObservable = () => this.coreService.data.executeReadObjectsQuery(queryMaster, true);

    return refreshData$.pipe(
      startWith(undefined),
      exhaustMap(() => {
        component.isLoading = true;
        component.errorMessage = '';
        setDataItem(undefined);
        reset();
        const queries: (() => Observable<ArrayBuffer>)[] = [];
        queries.push(queryMasterObservable);
        queryMaster.Parameters = [];

        return this.querySettings().pipe(
          exhaustMap(() => {
            queryMaster.Parameters.push(new QueryParameter('allowWindowsAuth', this.contextService.allowWindowsAuth));
            return forkJoin(queries.map((query) => query())).pipe(
              map((data) => {
                const id = this.contextService.currentUserId;
                handleData(data, id);
                validate();
              })
            );
          }),
          catchError((error) => {
            component.errorMessage = this.coreService.error.getFullErrorMessage(error);
            this.coreService.error.publish(error);

            return of(undefined);
          }),
          tap(() => (component.isLoading = false))
        );
      })
    );
  }

  private querySettings(): Observable<void> {
    return forkJoin([
      this.authService.current.pipe(take(1)),
      this.userService.getCurrentLoginInfo().pipe(take(1)),
    ]).pipe(
      map((result) => {
        this.contextService.authConfiguration = result[0];
        this.contextService.loginInfo = result[1];
      })
    );
  }

  private createMUserQuery(opath?: string, name?: string): Query {
    return this.createMUserQueryCore(opath ?? 'Type(MUser)[Type=0 AND PrivilegeType=0]', name ?? 'Users');
  }

  private createMUserQueryCore(opath: string, name: string): Query {
    return this.createQuery(opath, name, (query) => {
      query.Columns = [
        this.coreService.data.createQueryColumn('Name', '@DisplayText', QueryColumnSortOrder.Ascending),
        this.coreService.data.createQueryColumn('Id'),
        this.coreService.data.createQueryColumn('ParentId'),
        this.coreService.data.createQueryColumn('EMail'),
        this.coreService.data.createQueryColumn('Login'),
        this.coreService.data.createQueryColumn('LoginMode'),
        this.coreService.data.createQueryColumn('MaxSessionsCount'),
        this.coreService.data.createQueryColumn('MinSessionsCount'),
        this.coreService.data.createQueryColumn('Name'),
        this.coreService.data.createQueryColumn('PasswordHash'),
      ];
    });
  }

  private createQuery(opath: string, name: string, prepareQuery: (q: Query) => void): Query {
    const query = new Query();
    query.OPath = opath;
    query.Name = name;
    prepareQuery(query);

    return query;
  }

  private setUsers(users: any, id?: string): UserViewModel | undefined {
    return this.setDataItems(
      users,
      id,
      this.contextService.users,
      (dataItem) => new UserViewModel(this.contextService, new MUser(dataItem))
    );
  }

  private setDataItems<T extends ViewModelBase>(
    dataItems: any,
    id: string | undefined,
    collection: ArrayMap<string, T>,
    createViewModel: (dataItem: any) => T
  ): T | undefined {
    let result;

    if (dataItems) {
      for (const dataItem of dataItems) {
        const viewModel = createViewModel(dataItem);
        collection.set(viewModel);

        if (viewModel.id === id) {
          result = viewModel;
        }
      }
    }

    return result;
  }

  persistData(
    component: {
      isSaving: boolean;
    },
    saveData$: Subject<PersistData>,
    refreshData$: Subject<void>
  ): Observable<void> {
    return saveData$.pipe(
      exhaustMap((persistData) =>
        this.showMessageBox(persistData).pipe(
          exhaustMap((dialogResult) => {
            if (dialogResult !== DialogResult.Yes) {
              return of(undefined);
            }

            component.isSaving = true;
            let _id: string | undefined = '';

            return persistData.createObservable().pipe(
              map((id) => {
                if (id) {
                  _id = id;
                }

                if (!persistData.noRefresh) {
                  refreshData$.next();
                }
              }),
              catchError((error) => {
                _id = undefined;
                this.coreService.error.publish(error);

                return of(undefined);
              }),
              tap(() => {
                component.isSaving = false;

                if (persistData.continueWith && _id !== undefined) {
                  persistData.continueWith();
                }
              })
            );
          })
        )
      )
    );
  }

  persistUser(user: UserViewModel): Observable<string> {
    if (!user.canSave) {
      return of(user.id);
    }

    const persistModel = this.createPersistModel();
    persistModel.Objects[0].push(this.createMUserPersistData(user.muser));
    return this.coreService.data.executePersistObjectsQuery(persistModel).pipe(map((data) => user.id));
  }

  private createMUserPersistData(user: MUser): TargetObjectData {
    return this.createPersistData('MUser', user.Id, (data) => {
      data.TargetColumns.push(
        {
          Name: 'EMail',
          Value: <any>user.EMail,
        },
        {
          Name: 'Name',
          Value: <any>user.Name,
        }
      );
    });
  }

  private createPersistModel(): PersistObjectsModel {
    const persistObjectsModel = new PersistObjectsModel();
    persistObjectsModel.ObjectSpaceName = this.objectSpaceMaster;
    persistObjectsModel.Objects = [];
    persistObjectsModel.Objects.push([]);

    return persistObjectsModel;
  }

  private createPersistData(
    objectType: string,
    id: string,
    prepareData: (data: TargetObjectData) => void
  ): TargetObjectData {
    const targetObjectData = new TargetObjectData();
    targetObjectData.ObjectKey = new ObjectKey();
    targetObjectData.ObjectKey.ObjectType = objectType;
    targetObjectData.ObjectKey.Id = id;

    targetObjectData.Mode = PersistMode.Update;
    targetObjectData.TargetColumns = [];
    prepareData(targetObjectData);

    return targetObjectData;
  }

  private showMessageBox(persistData: PersistData): Observable<DialogResult> {
    if (!persistData.messageBoxMessage) {
      return of(DialogResult.Yes);
    }

    const settings = new MessageSettings();
    settings.mode = MessageMode.Warning;
    settings.button = MessageBoxButtons.YesNo;
    settings.message = persistData.messageBoxMessage;
    settings.caption = persistData.messageBoxCaption ?? '';
    return this.messageBoxService.showMessageBox(settings).pipe(map((dialogResult) => (<any>dialogResult).result));
  }
}

export class PersistData {
  continueWith?: () => void;
  noRefresh?: boolean;

  constructor(
    readonly createObservable: () => Observable<string>,
    readonly messageBoxMessage?: string,
    readonly messageBoxCaption?: string
  ) {}
}
